/* ============================================================================
   UMAI · B3G1 — motion + overlay plumbing                 umai-b3g1-motion.js
   Owner: DESIGN agent. Namespace: `window.UB3`, `data-ub3-*` attributes only.

   This is the design system's behaviour layer, not the bundle's. It owns
   exactly four things that are presentation concerns and that neither BUNDLE
   nor COMPOSITION should have to re-implement:

     1. scroll reveal          [data-ub3-reveal]
     2. the mobile sticky bar  [data-ub3-bar] + [data-ub3-bar-anchor]
     3. sheet / modal a11y     [data-ub3-sheet-open] / .ub3-sheet
     4. toasts                 UB3.toast('ADDED')

   It does NOT touch the cart, prices, variants or the catalogue. If you need
   any of that, it belongs in umai-b3g1-bundle.js.

   Everything is opt-in via a data attribute — dropping this file on a page
   with none of them present is a no-op. Safe to load `defer`, and safe to load
   twice (guarded). Respects prefers-reduced-motion throughout.
   ========================================================================== */
(function () {
  'use strict';
  if (window.UB3 && window.UB3.__init) return;

  var reduced = false;
  try {
    var mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    reduced = mq.matches;
    var onMQ = function (e) { reduced = e.matches; };
    if (mq.addEventListener) mq.addEventListener('change', onMQ);
    else if (mq.addListener) mq.addListener(onMQ);
  } catch (e) { /* very old browser: treat as motion-allowed */ }

  var UB3 = window.UB3 = window.UB3 || {};
  UB3.__init = true;
  UB3.reducedMotion = function () { return reduced; };

  var $$ = function (sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  };

  /* ------------------------------------------------------------------ 1. reveal
     Adds .ub3-in once, when the element first enters the viewport. One shot —
     nothing re-animates on scroll-back, nothing loops. Under reduced motion we
     skip the observer entirely and leave the element in its final state (the
     CSS `.ub3-in` keyframe is already neutralised there, but not creating the
     observer at all is cheaper and avoids any flash).
  ---------------------------------------------------------------------------- */
  function initReveal() {
    var nodes = $$('[data-ub3-reveal]:not([data-ub3-seen])');
    if (!nodes.length) return;
    if (reduced || typeof IntersectionObserver === 'undefined') {
      nodes.forEach(function (n) { n.setAttribute('data-ub3-seen', ''); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var delay = parseInt(el.getAttribute('data-ub3-reveal'), 10);
        if (delay > 0) el.style.animationDelay = Math.min(delay, 400) + 'ms';
        el.classList.add('ub3-in');
        el.setAttribute('data-ub3-seen', '');
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    nodes.forEach(function (n) { io.observe(n); });
  }

  /* --------------------------------------------------------------- 2. stickybar
     The bar hides while the real in-page CTA is on screen (two identical CTAs
     visible at once reads as a bug) and slides in once that anchor scrolls
     away. Purely presentational — the bar's contents are COMPOSITION's.
     Uses an observer on the anchor rather than a scroll handler, so it costs
     nothing on scroll.
  ---------------------------------------------------------------------------- */
  function initBar() {
    var bar = document.querySelector('[data-ub3-bar]');
    if (!bar) return;
    var anchor = document.querySelector('[data-ub3-bar-anchor]');
    var setHidden = function (hidden) {
      bar.setAttribute('data-hidden', hidden ? 'true' : 'false');
      bar.setAttribute('aria-hidden', hidden ? 'true' : 'false');
      // keep the bar out of the tab order while it is off-screen
      $$('a,button,input,select', bar).forEach(function (el) {
        if (hidden) { el.setAttribute('tabindex', '-1'); }
        else { el.removeAttribute('tabindex'); }
      });
    };
    if (!anchor || typeof IntersectionObserver === 'undefined') {
      setHidden(false);
      return;
    }
    setHidden(true);
    new IntersectionObserver(function (entries) {
      setHidden(entries[0].isIntersecting);
    }, { threshold: 0 }).observe(anchor);
  }

  /* ------------------------------------------------------------------ 3. sheet
     Bottom sheet on mobile / centred dialog above 768px. Handles the parts
     that are easy to get wrong and that are a genuine accessibility
     requirement: scrim, Escape, click-outside, body scroll lock, focus move-in,
     focus trap, and focus restore to the trigger on close.

       <button data-ub3-sheet-open="pick">…</button>
       <div class="ub3-scrim" data-ub3-scrim="pick"></div>
       <div class="ub3-sheet" id="pick" role="dialog" aria-modal="true"
            aria-labelledby="pick-title" hidden> … <button data-ub3-sheet-close>
  ---------------------------------------------------------------------------- */
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),' +
                  'select:not([disabled]),textarea:not([disabled]),' +
                  '[tabindex]:not([tabindex="-1"])';
  var openSheet = null;
  var lastTrigger = null;
  var scrollY = 0;

  function lockBody() {
    scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    // BUNDLE's JS locks document.documentElement and sections/product-b3g1
    // guards on `html.ub3-lock`. Set BOTH so either author's rule fires.
    document.documentElement.classList.add('ub3-lock');
    document.body.classList.add('ub3-lock');
    document.body.style.position = 'fixed';
    document.body.style.top = -scrollY + 'px';
    document.body.style.width = '100%';
  }
  function unlockBody() {
    document.documentElement.classList.remove('ub3-lock');
    document.body.classList.remove('ub3-lock');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);
  }

  UB3.openSheet = function (id, trigger) {
    var sheet = document.getElementById(id);
    if (!sheet || openSheet === sheet) return;
    if (openSheet) UB3.closeSheet();
    lastTrigger = trigger || document.activeElement;
    openSheet = sheet;
    sheet.hidden = false;
    var scrim = document.querySelector('[data-ub3-scrim="' + id + '"]');
    if (scrim) { scrim.hidden = false; }
    lockBody();
    // next frame so the transition actually runs from its start state
    requestAnimationFrame(function () {
      sheet.setAttribute('data-open', 'true');
      if (scrim) scrim.setAttribute('data-open', 'true');
      var first = sheet.querySelector('[data-ub3-autofocus]') ||
                  sheet.querySelector(FOCUSABLE);
      if (first) { try { first.focus({ preventScroll: true }); } catch (e) { first.focus(); } }
      else { sheet.setAttribute('tabindex', '-1'); sheet.focus(); }
    });
    sheet.dispatchEvent(new CustomEvent('ub3:sheet:open', { bubbles: true }));
  };

  UB3.closeSheet = function () {
    var sheet = openSheet;
    if (!sheet) return;
    openSheet = null;
    var id = sheet.id;
    var scrim = document.querySelector('[data-ub3-scrim="' + id + '"]');
    sheet.setAttribute('data-open', 'false');
    if (scrim) scrim.setAttribute('data-open', 'false');
    unlockBody();
    var finish = function () {
      sheet.hidden = true;
      if (scrim) scrim.hidden = true;
    };
    if (reduced) finish();
    else window.setTimeout(finish, 340); // matches --ub3-d-3
    if (lastTrigger && document.contains(lastTrigger)) {
      try { lastTrigger.focus({ preventScroll: true }); } catch (e) { lastTrigger.focus(); }
    }
    lastTrigger = null;
    sheet.dispatchEvent(new CustomEvent('ub3:sheet:close', { bubbles: true }));
  };

  function onKeydown(e) {
    if (!openSheet) return;
    if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); UB3.closeSheet(); return; }
    if (e.key !== 'Tab') return;
    var items = $$(FOCUSABLE, openSheet).filter(function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
    });
    if (!items.length) return;
    var first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function onClick(e) {
    var opener = e.target.closest && e.target.closest('[data-ub3-sheet-open]');
    if (opener) {
      e.preventDefault();
      UB3.openSheet(opener.getAttribute('data-ub3-sheet-open'), opener);
      return;
    }
    if (e.target.closest && e.target.closest('[data-ub3-sheet-close]')) {
      e.preventDefault(); UB3.closeSheet(); return;
    }
    if (e.target.hasAttribute && e.target.hasAttribute('data-ub3-scrim')) {
      UB3.closeSheet();
    }
  }

  /* ------------------------------------------------------------------ 4. toast
     One at a time, auto-dismissing, announced politely. Never used for errors
     that the shopper must act on — those belong inline, next to the control.
  ---------------------------------------------------------------------------- */
  var toastEl = null, toastTimer = null;
  UB3.toast = function (message, ms) {
    if (!message) return;
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'ub3-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = String(message);
    requestAnimationFrame(function () { toastEl.setAttribute('data-open', 'true'); });
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () {
      toastEl.setAttribute('data-open', 'false');
    }, ms || 2600);
  };

  /* ---------------------------------------------------------------------- init */
  function init() {
    initReveal();
    initBar();
  }
  UB3.refresh = init; // call after injecting new markup (e.g. the art wall)

  document.addEventListener('keydown', onKeydown, true);
  document.addEventListener('click', onClick);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Shopify theme editor re-renders sections without a page load
  document.addEventListener('shopify:section:load', init);
})();
